import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  CaseAudit,
  Demography,
  PatientAssessmentCase,
  ProvisionalDisease,
  UserRole,
  Vitals,
  VisitRecord,
} from './patient-assessment.models';

interface ScoreBox {
  score: number;
  reasons: string[];
}

const INITIAL_CASE: PatientAssessmentCase = {
  demography: {
    patientId: '',
    fullName: '',
    age: null,
    sex: '',
    phone: '',
    email: '',
    address: '',
    occupation: '',
  },
  vitals: {
    bpSystolic: null,
    bpDiastolic: null,
    pulse: null,
    temperature: null,
    respiratoryRate: null,
    spo2: null,
    weight: null,
    height: null,
    painScore: null,
  },
  chiefComplaints: [],
  medicalHistory: [],
  investigationReports: [],
  provisionalDiseases: [],
  selectedDiagnoses: [],
  investigationPlan: [],
  advicePlan: [],
  treatmentPlan: [],
  nextVisit: '',
  followUpNotes: '',
  visits: [],
  audits: [],
};

@Injectable({ providedIn: 'root' })
export class AssessmentService {
  private readonly role: { value: UserRole } = { value: 'Doctor' };
  private readonly subject = new BehaviorSubject<PatientAssessmentCase>(this.recalculate(this.clone(INITIAL_CASE)));
  readonly case$ = this.subject.asObservable();

  get snapshot(): PatientAssessmentCase {
    return this.subject.value;
  }

  setRole(role: UserRole): void {
    this.role.value = role;
    this.recordAudit('role-change', `Active role changed to ${role}`);
  }

  updateDemography(patch: Partial<Demography>): void {
    this.applyChange('demography-update', 'Patient demographic information updated', (state) => {
      state.demography = { ...state.demography, ...patch };
    });
  }

  updateVitals(patch: Partial<Vitals>): void {
    this.applyChange('vitals-update', 'Vital signs updated', (state) => {
      state.vitals = { ...state.vitals, ...patch };
    });
  }

  addChiefComplaint(value: string): void {
    const item = value.trim();
    if (!item) return;
    this.applyChange('chief-complaint-add', `Added chief complaint: ${item}`, (state) => {
      if (!state.chiefComplaints.includes(item)) {
        state.chiefComplaints.push(item);
      }
    });
  }

  removeChiefComplaint(index: number): void {
    this.applyChange('chief-complaint-remove', `Removed chief complaint at index ${index}`, (state) => {
      state.chiefComplaints.splice(index, 1);
    });
  }

  addMedicalHistory(value: string): void {
    const item = value.trim();
    if (!item) return;
    this.applyChange('history-add', `Added medical history item: ${item}`, (state) => {
      if (!state.medicalHistory.includes(item)) {
        state.medicalHistory.push(item);
      }
    });
  }

  removeMedicalHistory(index: number): void {
    this.applyChange('history-remove', `Removed medical history at index ${index}`, (state) => {
      state.medicalHistory.splice(index, 1);
    });
  }

  addMedicalDiagnosis(value: string): void {
    const item = value.trim();
    if (!item) return;
    this.applyChange('dianosis-add', `Added medical dianosis item: ${item}`, (state) => {
      if (!state.selectedDiagnoses.includes(item)) {
        state.selectedDiagnoses.push(item);
      }
    });
  }

  removeMedicalDianosis(index: number): void {
    this.applyChange('dianosis-remove', `Removed medical diagnosis at index ${index}`, (state) => {
      state.selectedDiagnoses.splice(index, 1);
    });
  }

  toggleDiagnosis(name: string): void {
    this.applyChange('diagnosis-toggle', `Toggled suspected diagnosis: ${name}`, (state) => {
      const idx = state.selectedDiagnoses.indexOf(name);
      if (idx >= 0) {
        state.selectedDiagnoses.splice(idx, 1);
      } else {
        state.selectedDiagnoses.push(name);
      }
    });
  }

  addInvestigation(value: string): void {
    const item = value.trim();
    if (!item) return;
    this.applyChange('investigation-add', `Added investigation item: ${item}`, (state) => {
      if (!state.investigationPlan.includes(item)) {
        state.investigationPlan.push(item);
      }
    }, false);
  }

  removeInvestigation(index: number): void {
    this.applyChange('investigation-remove', `Removed investigation at index ${index}`, (state) => {
      state.investigationPlan.splice(index, 1);
    }, false);
  }

  addAdvice(value: string): void {
    const item = value.trim();
    if (!item) return;
    this.applyChange('advice-add', `Added advice item: ${item}`, (state) => {
      if (!state.advicePlan.includes(item)) {
        state.advicePlan.push(item);
      }
    }, false);
  }

  removeAdvice(index: number): void {
    this.applyChange('advice-remove', `Removed advice at index ${index}`, (state) => {
      state.advicePlan.splice(index, 1);
    }, false);
  }

  addTreatment(value: string): void {
    const item = value.trim();
    if (!item) return;
    this.applyChange('treatment-add', `Added treatment item: ${item}`, (state) => {
      if (!state.treatmentPlan.includes(item)) {
        state.treatmentPlan.push(item);
      }
    }, false);
  }

  removeTreatment(index: number): void {
    this.applyChange('treatment-remove', `Removed treatment at index ${index}`, (state) => {
      state.treatmentPlan.splice(index, 1);
    }, false);
  }

  scheduleFollowUp(date: string, note: string, reports: string[]): void {
    const followUpDate = date || new Date().toISOString().slice(0, 10);
    this.applyChange('follow-up-scheduled', `Scheduled follow-up visit on ${followUpDate}`, (state) => {
      state.nextVisit = followUpDate;
      state.followUpNotes = note.trim();
      const cleanedReports = reports.map((r) => r.trim()).filter(Boolean);
      if (cleanedReports.length) {
        state.investigationReports = this.unique([...cleanedReports, ...state.investigationReports]);
      }
      const visit: VisitRecord = {
        id: `${Date.now()}`,
        date: followUpDate,
        note: note.trim(),
        reports: cleanedReports,
      };
      state.visits = [visit, ...state.visits];
    }, false);
  }

  private applyChange(
    action: string,
    detail: string,
    mutator: (state: PatientAssessmentCase) => void,
    recalculatePlans = true
  ): void {
    const next = this.clone(this.snapshot);
    mutator(next);
    if (recalculatePlans) {
      this.recalculate(next);
    }
    next.audits = [this.makeAudit(action, detail), ...next.audits].slice(0, 200);
    this.subject.next(next);
  }

  private recordAudit(action: string, detail: string): void {
    const next = this.clone(this.snapshot);
    next.audits = [this.makeAudit(action, detail), ...next.audits].slice(0, 200);
    this.subject.next(next);
  }

  private makeAudit(action: string, detail: string): CaseAudit {
    return {
      at: new Date().toLocaleString(),
      by: this.role.value,
      action,
      detail,
    };
  }

  private recalculate(state: PatientAssessmentCase): PatientAssessmentCase {
    state.provisionalDiseases = this.scoreDiseases(state);

    const sources = state.selectedDiagnoses.length > 0
      ? state.selectedDiagnoses
      : state.provisionalDiseases.slice(0, 2).map((d) => d.name);

    const plan = this.buildPlan(sources);
    state.investigationPlan = plan.investigations;
    state.advicePlan = plan.advices;
    state.treatmentPlan = plan.treatments;
    return state;
  }

  private buildPlan(diagnoses: string[]): { investigations: string[]; advices: string[]; treatments: string[] } {
    if (!diagnoses.length) {
      return {
        investigations: [
          'Complete history and physical examination',
          'Repeat vital signs if needed',
          'Confirm red-flag symptoms',
        ],
        advices: [
          'Review symptoms carefully',
          'Advise urgent review if symptoms worsen',
          'Arrange follow-up as clinically appropriate',
        ],
        treatments: [
          'Supportive care as clinically indicated',
          'Document the assessment plan',
          'Reassess after additional information becomes available',
        ],
      };
    }

    const planMap: Record<string, { investigations: string[]; advices: string[]; treatments: string[] }> = {
      'Acute coronary syndrome': {
        investigations: ['ECG', 'Troponin', 'CBC', 'Chest X-ray', 'Repeat BP and pulse monitoring'],
        advices: ['আমার নাম কি','Limit exertion until reviewed', 'Seek urgent care if chest pain worsens', 'Monitor red flags closely'],
        treatments: ['Cardiology review', 'Antiplatelet discussion if appropriate', 'Antianginal management as clinically indicated'],
      },
      Pneumonia: {
        investigations: ['Chest X-ray', 'CBC', 'CRP', 'Pulse oximetry', 'Sputum investigation if indicated'],
        advices: ['আমার নাম কি','Hydration', 'Rest', 'Return urgently if dyspnea worsens'],
        treatments: ['Antibiotic plan per local protocol', 'Fever control as appropriate', 'Supportive respiratory care'],
      },
      'Asthma / COPD exacerbation': {
        investigations: ['Pulse oximetry', 'Peak flow or spirometry if appropriate', 'Chest X-ray if needed'],
        advices: ['আমার নাম কি','Avoid triggers', 'Use inhaler technique correctly', 'Return urgently if shortness of breath increases'],
        treatments: ['Bronchodilator plan', 'Steroid discussion if appropriate', 'Respiratory follow-up'],
      },
      'GERD / reflux disease': {
        investigations: ['Clinical review', 'Consider reflux evaluation', 'H. pylori test if indicated'],
        advices: ['আমার নাম কি','Avoid large meals', 'Avoid late-night eating', 'Elevate the head while sleeping'],
        treatments: ['PPI trial if suitable', 'Lifestyle modification', 'Follow-up if symptoms persist'],
      },
      'Musculoskeletal pain': {
        investigations: ['Physical examination', 'ECG if chest pain remains uncertain', 'Imaging if trauma is suspected'],
        advices: ['Rest the affected area', 'Heat or cold pack if appropriate', 'Review pain red flags'],
        treatments: ['Analgesia as appropriate', 'Physiotherapy advice if needed', 'Observation and reassessment'],
      },
      'Migraine / primary headache': {
        investigations: ['Neurological exam', 'Blood pressure check', 'Brain imaging if red flags are present'],
        advices: ['Hydration', 'Quiet/dark environment', 'Monitor for neurological warning signs'],
        treatments: ['Analgesic management', 'Migraine-specific treatment if suitable', 'Follow-up if headaches persist'],
      },
      'Hypertensive urgency': {
        investigations: ['Repeat blood pressure', 'ECG', 'Renal function', 'Urinalysis'],
        advices: ['Salt reduction', 'Medication adherence review', 'Urgent review if symptoms worsen'],
        treatments: ['Blood pressure management per protocol', 'Medication optimization', 'Close follow-up'],
      },
      'Appendicitis / acute abdomen': {
        investigations: ['Abdominal examination', 'CBC', 'Ultrasound/CT as clinically indicated'],
        advices: ['Avoid heavy meals until reviewed', 'Return urgently if pain worsens', 'Monitor for fever or vomiting'],
        treatments: ['Surgical review if appropriate', 'Pain control as advised', 'Observation and escalation'],
      },
      'Sepsis / serious infection': {
        investigations: ['CBC', 'CRP', 'Blood cultures', 'Urinalysis', 'Chest X-ray'],
        advices: ['Urgent escalation', 'Hydration', 'Monitor temperature and breathing'],
        treatments: ['Immediate physician review', 'Protocol-based antimicrobial management', 'Supportive care and monitoring'],
      },
    };

    const set = new Set<string>();
    const investigations: string[] = [];
    const advices: string[] = [];
    const treatments: string[] = [];

    for (const diagnosis of diagnoses) {
      const plan = planMap[diagnosis];
      if (!plan) continue;
      plan.investigations.forEach((item) => {
        if (!set.has(`i:${item}`)) {
          set.add(`i:${item}`);
          investigations.push(item);
        }
      });
      plan.advices.forEach((item) => {
        if (!set.has(`a:${item}`)) {
          set.add(`a:${item}`);
          advices.push(item);
        }
      });
      plan.treatments.forEach((item) => {
        if (!set.has(`t:${item}`)) {
          set.add(`t:${item}`);
          treatments.push(item);
        }
      });
    }

    if (!investigations.length) investigations.push('Complete assessment and clinical review');
    if (!advices.length) advices.push('Continue observation and reassessment');
    if (!treatments.length) treatments.push('Supportive treatment as clinically indicated');

    return { investigations, advices, treatments };
  }

  private scoreDiseases(state: PatientAssessmentCase): ProvisionalDisease[] {
    const boxes: Record<string, ScoreBox> = {
      'Acute coronary syndrome': { score: 0, reasons: [] },
      Pneumonia: { score: 0, reasons: [] },
      'GERD / reflux disease': { score: 0, reasons: [] },
      'Musculoskeletal pain': { score: 0, reasons: [] },
      'Asthma / COPD exacerbation': { score: 0, reasons: [] },
      'Migraine / primary headache': { score: 0, reasons: [] },
      'Hypertensive urgency': { score: 0, reasons: [] },
      'Appendicitis / acute abdomen': { score: 0, reasons: [] },
      'Sepsis / serious infection': { score: 0, reasons: [] },
    };

    const add = (name: keyof typeof boxes, score: number, reason: string) => {
      boxes[name].score += score;
      boxes[name].reasons.push(reason);
    };

    const complaintText = state.chiefComplaints.join(' ').toLowerCase();
    const historyText = state.medicalHistory.join(' ').toLowerCase();
    const fullText = `${complaintText} ${historyText}`;
    const v = state.vitals;

    const has = (term: string) => fullText.includes(term);

    if (complaintText.includes('chest pain') || complaintText.includes('chest tightness') || complaintText.includes('pressure')) {
      add('Acute coronary syndrome', 60, 'Chest pain / pressure pattern');
      add('GERD / reflux disease', 18, 'Chest discomfort may be reflux-related');
      add('Musculoskeletal pain', 14, 'Chest wall pain remains possible');
      add('Hypertensive urgency', 10, 'Chest pain with possible blood pressure involvement');
    }

    if (complaintText.includes('cough') || complaintText.includes('shortness of breath') || complaintText.includes('dyspnea')) {
      add('Pneumonia', 35, 'Respiratory symptoms present');
      add('Asthma / COPD exacerbation', 30, 'Cough or dyspnea may indicate airway disease');
      add('Sepsis / serious infection', 20, 'Respiratory infection can progress to systemic illness');
    }

    if (complaintText.includes('fever') || (v.temperature !== null && v.temperature >= 38)) {
      add('Pneumonia', 25, 'Fever is consistent with infection');
      add('Sepsis / serious infection', 40, 'Fever may indicate serious infection');
      add('Appendicitis / acute abdomen', 12, 'Fever can accompany acute abdomen');
    }

    if (complaintText.includes('headache') || complaintText.includes('migraine')) {
      add('Migraine / primary headache', 35, 'Headache complaint present');
      add('Hypertensive urgency', 22, 'Headache can occur with severe hypertension');
    }

    if (complaintText.includes('abdominal pain') || complaintText.includes('abdomen') || complaintText.includes('stomach')) {
      add('Appendicitis / acute abdomen', 35, 'Abdominal pain complaint present');
      add('GERD / reflux disease', 16, 'Upper abdominal discomfort can be reflux-related');
    }

    if (v.bpSystolic !== null && v.bpDiastolic !== null) {
      if (v.bpSystolic >= 180 || v.bpDiastolic >= 110) {
        add('Hypertensive urgency', 55, 'Markedly elevated blood pressure');
      }
    }

    if (v.spo2 !== null && v.spo2 < 94) {
      add('Pneumonia', 25, 'Low oxygen saturation');
      add('Asthma / COPD exacerbation', 28, 'Low oxygen saturation');
      add('Sepsis / serious infection', 18, 'Hypoxia can occur in serious infection');
    }

    if (v.pulse !== null && v.pulse >= 100) {
      add('Acute coronary syndrome', 8, 'Tachycardia may accompany ischemia');
      add('Pneumonia', 8, 'Tachycardia may accompany infection');
      add('Sepsis / serious infection', 10, 'Tachycardia may indicate systemic infection');
    }

    if (v.temperature !== null && v.temperature >= 38.5) {
      add('Sepsis / serious infection', 18, 'High fever increases infection concern');
      add('Pneumonia', 12, 'High fever can accompany pneumonia');
    }

    if (v.painScore !== null && v.painScore >= 7 && complaintText.includes('chest pain')) {
      add('Acute coronary syndrome', 15, 'Severe chest pain increases concern');
    }

    if (has('hypertension')) {
      add('Hypertensive urgency', 14, 'History of hypertension');
      add('Acute coronary syndrome', 12, 'Hypertension increases cardiovascular risk');
    }

    if (has('diabetes')) {
      add('Acute coronary syndrome', 10, 'Diabetes increases cardiovascular risk');
      add('Sepsis / serious infection', 8, 'Diabetes increases infection risk');
    }

    if (has('smoking')) {
      add('Acute coronary syndrome', 12, 'Smoking increases cardiovascular risk');
      add('Asthma / COPD exacerbation', 10, 'Smoking increases airway disease risk');
    }

    if (has('asthma') || has('copd')) {
      add('Asthma / COPD exacerbation', 25, 'History of airway disease');
    }

    if (has('peptic') || has('gastritis') || has('reflux')) {
      add('GERD / reflux disease', 20, 'History suggests reflux disease');
    }

    if (has('infection') || has('fever')) {
      add('Sepsis / serious infection', 12, 'History suggests possible infection');
    }

    const ranked = Object.entries(boxes)
      .filter(([, value]) => value.score > 0)
      .sort((a, b) => b[1].score - a[1].score);

    if (!ranked.length) {
      return [];
    }

    const max = ranked[0][1].score;
    return ranked.slice(0, 6).map(([name, box]) => ({
      name,
      probability: Math.max(5, Math.min(99, Math.round((box.score / max) * 100))),
      reason: box.reasons.slice(0, 3).join(' • '),
    }));
  }

  private unique(values: string[]): string[] {
    return Array.from(new Set(values));
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
